import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import Button from '@dashboard/components/Button';
import Dialog from '@dashboard/components/Dialog';
import FieldExample from './FieldExample';
import PickerItemIcon from './PickerItemIcon';
import ConditionalBranchIcon, { TRIGGER_LABEL } from './ConditionalBranchIcon';
import { TRIGGER_NOTE, pickerSections, startsListGroup } from './pickerItems';

export const pickerTitle = ({ mode, groupId, guestQuestions = false }) => {
  if (mode === 'change') return 'Field type';
  if (guestQuestions) return 'Add question';
  return groupId ? 'Add to group' : 'Add to form';
};

// `change` mode picks a new type for an existing field. A `groupId` hides Group: groups never nest.
export default function AddPickerDialog({
  open,
  onOpenChange,
  onConfirm,
  mode = 'add',
  groupId = null,
  allowedElements,
  fields,
  currentKey,
  initialId,
  discountCode = null,
  guestQuestions = false,
  layoutBlocks = true,
}) {
  const sections = useMemo(
    () =>
      pickerSections({
        mode,
        groupId,
        allowedElements,
        fields,
        currentKey,
        discountCode,
        guestQuestions,
        layoutBlocks,
      }),
    [
      mode,
      groupId,
      allowedElements,
      fields,
      currentKey,
      discountCode,
      guestQuestions,
      layoutBlocks,
    ],
  );
  const enabled = sections.flatMap(section => section.items).filter(item => !item.disabledReason);

  const [selectedId, setSelectedId] = useState(
    () => enabled.find(item => item.id === (initialId ?? 'text'))?.id ?? enabled[0]?.id,
  );
  const selected = enabled.find(item => item.id === selectedId) ?? enabled[0];
  const optionRefs = useRef({});

  const select = id => {
    setSelectedId(id);
    optionRefs.current[id]?.focus();
  };

  const onKeyDown = event => {
    const index = enabled.findIndex(item => item.id === selected?.id);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = enabled[(index + step + enabled.length) % enabled.length];
      if (next) select(next.id);
    } else if (event.key === 'Enter' && selected) {
      event.preventDefault();
      onConfirm(selected);
    }
  };

  const renderOption = item => {
    const isSelected = item.id === selected?.id;
    return (
      <div
        key={item.id}
        ref={node => {
          optionRefs.current[item.id] = node;
        }}
        role="option"
        aria-selected={isSelected}
        aria-disabled={item.disabledReason ? true : undefined}
        tabIndex={isSelected ? 0 : -1}
        onClick={() => !item.disabledReason && setSelectedId(item.id)}
        onDoubleClick={() => !item.disabledReason && onConfirm(item)}
        className={clsx(
          'flex min-h-7 shrink-0 cursor-default items-start gap-2 rounded-md px-2 py-1.5 text-[13px] leading-4 font-medium outline-none select-none',
          isSelected && 'bg-sidebar-accent text-sidebar-accent-foreground',
          !isSelected && !item.disabledReason && 'hover:bg-sidebar-accent',
          item.disabledReason && 'text-muted-foreground/60',
        )}
      >
        <PickerItemIcon item={item} />
        <span className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-2">
          <span>{item.label}</span>
          {item.disabledReason && (
            <span className="text-xs font-normal">{item.disabledReason}</span>
          )}
        </span>
        {!item.disabledReason && item.trigger && (
          <span title={TRIGGER_LABEL} className="text-muted-foreground/80">
            <ConditionalBranchIcon />
          </span>
        )}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="3xl"
      title={<span className="sr-only">{pickerTitle({ mode, groupId, guestQuestions })}</span>}
      // The buttons sit under the details, so the list can run the dialog's full height.
      cancelButton={null}
      confirmButton={null}
    >
      {/* Edge to edge: past the dialog's 24px padding and the body's 16px top margin. */}
      <div className="-mx-6 -mt-10 -mb-6 flex h-[min(800px,88vh)]">
        <div
          role="listbox"
          aria-label={mode === 'change' ? 'Field types' : 'What to add'}
          onKeyDown={onKeyDown}
          className="w-56 shrink-0 overflow-y-auto border-r py-2 pr-2 pl-4"
        >
          {sections.map((section, index) => (
            <div key={section.title} role="group" aria-label={section.title}>
              {index > 0 && (
                <div role="separator" className="mx-2 my-1 h-px bg-gray-100 dark:bg-muted" />
              )}
              <div className="flex h-7 items-center px-2 text-xs font-medium text-muted-foreground">
                {section.title}
              </div>
              {section.items.map((item, index) => (
                <React.Fragment key={item.id}>
                  {startsListGroup(section.items, index) && (
                    <div role="separator" className="mx-2 my-1 h-px bg-gray-100 dark:bg-muted" />
                  )}
                  {renderOption(item)}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          {selected && (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-5">
              <h3 className="pr-8 text-lg font-semibold text-gray-900 dark:text-foreground">
                {selected.label}
              </h3>
              <p className="mt-1 max-w-md text-sm text-gray-700 dark:text-muted-foreground">
                {selected.description}
              </p>
              {selected.note && (
                <p className="mt-2 text-xs text-muted-foreground">{selected.note}</p>
              )}
              {selected.trigger && (
                <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium text-foreground">
                    <ConditionalBranchIcon />
                    Conditional
                  </span>
                  {TRIGGER_NOTE}
                </p>
              )}
              <div className="mt-5 rounded-lg border bg-white p-5">
                <span className="mb-4 inline-block rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground">
                  Example
                </span>
                <FieldExample item={selected} />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 px-6 py-4">
            <Button appearance="secondary" handleClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!selected}
              handleClick={() => selected && onConfirm(selected)}
            >
              {mode === 'change' ? 'Use this type' : pickerTitle({ mode, groupId, guestQuestions })}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
